/**
 * @ngdoc directive
 * @name gazelle.components.filterBar.directive:gzFilterBar
 *
 * @param {filters} Array of filter objects
 * @param {filterCount=} object that contains the current filtered count
 * @param {removeCallback=} callback that is called when the user removes an individual filter.
 * @param {removeAllCallback=} callback that is called when the user removes ALL filters.
 *
 * @description
 * `<gz-filter-bar>` provides a standard display of applied filters.
 *
 * The filter bar allows the consumer to specify a list of applied filters.  It will display the filters and allow the user to remove
 * individual filters or all of the filters
 *
 * @example
 * ````html
 * <gz-filter-bar
 *  filters='vm.filters'
 *  filter-count='vm.filterCount'
 *  remove-callback='vm.removeFilter'
 *  remove-all-callback='vm.removeAllFilters'>
 * </gz-filter-bar>
 * ````
 */
export const name = 'gzFilterBar';
export default function gzFilterBar() {
  //noinspection BadExpressionStatementJS
  'ngInject';

  let directive = {
    restrict: 'E',
    scope: {
      gridOptions: '=',
      filterCount: '=?',
      filters: '=?',
      counts: '=?',
      pairingId: '@',
      emphasisBackground: '=?',
      filterMode: '=?',
    },
    template: require('./filter-bar.jade'),
    link: gzFilterBarLink,
    controllerAs: 'vm',
    bindToController: true,
    controller: gzFilterBarController
  };

  return directive;

  /////////////////

  function gzFilterBarLink(scope, element, attrs) {
    scope.vm.readonly = 'readonly' in attrs;
  }

  // @ngInject
  function gzFilterBarController(
    $translate,
    $scope,
    $rootScope,
    moment,
    $gzFilters,
    $gzFields,
    $gzAnalytics,
    rnHttp,
    $log,
    $mdToast,
    $filter
  ) {
    let vm = this;

    const QUICK_FILTER = 'GZ_QUICK_FILTER';
    const USER_FILTER = 'GZ_USER_FILTER';
    const ADHOC_FILTER = 'GZ_ADHOC_FILTER';
    const NAMED_FILTER = 'GZ_NAMED_FILTER';
    const MATCH_ANY = 'GZ_MATCH_ANY';
    const EXCLUDE = 'GZ_EXCLUDE';
    const PORTAL_SERVICE_NAME = 'portal_service';
    const FILTERS_PATH = 'filters';

    let filters;
    let userFilterListener;
    let namedFilterListener;
    let adhocFilterListener;
    let quickFilterListener;
    let filterCountListener;
    let savedFilterAddedListener;

    vm.displayFilters = [];
    vm.quickAccessFilters = [];
    vm.savedFilters = [];
    vm.namedFilters = [];
    vm.filtersApplied = false;

    vm.selectFilter = selectFilter;
    vm.selectFilter1 = selectFilter1;
    vm.selectFilterById = selectFilterById;
    vm.toggleFilter = toggleFilter;
    vm.toggleFilter1 = toggleFilter1;
    vm.removeFilter = removeFilter;
    vm.removeFilter1 = removeFilter1;
    vm.removeFilter2 = removeFilter2;
    vm.removeAllFilters = removeAllFilters;
    vm.isExpandable = isExpandable;
    vm.isExpanded = isExpanded;
    vm.isExpanded1 = isExpanded1;
    vm.canSave = canSave;
    vm.saveFilter = saveFilter;
    vm.gzSavedFilterClicked = gzSavedFilterClicked;
    vm.gzNamedFilterClicked = gzNamedFilterClicked;
    vm.gzRemoveFilterClicked = gzRemoveFilterClicked;
    vm.gzRemoveBuiltInFilterClicked = gzRemoveBuiltInFilterClicked;
    vm.gzFilterClicked = gzFilterClicked;

    vm.unitTest = {
      setFilters: function(f) {filters = f;},
      getFilters: function() {return filters;},
      handleUserFilterChanged: handleUserFilterChanged,
      handleNamedFilterChanged: handleNamedFilterChanged,
      handleAdhocFilterChanged: handleAdhocFilterChanged,
      handleQuickFilterChanged: handleQuickFilterChanged,
      handleFilterCountChanged: handleFilterCountChanged
    };

    vm.$onInit = onInit;
    return;


    //////////////////
    function onInit() {
      let allowFilters = _.get(vm.gridOptions, 'grid.options.allowFilters', true);
      vm.showFilter = allowFilters && hasColumnFilter();
      vm.truncationTooltipKey = _.get(vm.gridOptions, 'grid.options.filter.truncationTooltipKey');

      vm.itemsDisplay = $translate.instant(_.get(vm.gridOptions, 'grid.options.itemPluralKey', 'uxf.uxf.FilterItems'));
      vm.itemDisplay = $translate.instant(_.get(vm.gridOptions, 'grid.options.itemSingularKey', 'uxf.uxf.FilterItem'));

      if(vm.filterMode === undefined){
        vm.filterMode = false;
      }
      if (vm.filters) {
        filters = _.cloneDeep(vm.filters);
        if(filters.namedFilters){
          vm.namedFilters = _.sortBy(filters.namedFilters, filter => {
            return filter.display.title;
          });
        }
        $gzFilters.mergeFilters(filters);
      }
      else {
        filters = $gzFilters.createFiltersObject(vm.filters);
      }

      userFilterListener = $rootScope.$on('gzUserFilterChangedEvent', handleUserFilterChanged);
      namedFilterListener = $rootScope.$on('gzNamedFilterChangedEvent', handleNamedFilterChanged);
      adhocFilterListener = $rootScope.$on('gzAdhocFilterChangedEvent', handleAdhocFilterChanged);
      quickFilterListener = $rootScope.$on('gzQuickFilterChangedEvent', handleQuickFilterChanged);
      filterCountListener = $rootScope.$on('gzFilterCountUpdated', handleFilterCountChanged);
      savedFilterAddedListener = $rootScope.$on('gzSavedFilterAddedEvent', handleSavedFilterAdded);

      $scope.$on('$destroy', () => {
        userFilterListener();
        namedFilterListener();
        adhocFilterListener();
        quickFilterListener();
        filterCountListener();
        savedFilterAddedListener();
      });

      $translate.onReady().then(() => {
        loadSavedFilters();
        updateFilters();
        updateQuickAccessFilters();
      });
    }

    function hasColumnFilter() {
      if (!_.get(vm, 'gridOptions.columns')) {
        return false;
      }

      let hasFilters = false;
      _.forEach(vm.gridOptions.columns, column => {
        let allowFilter = _.get(column, 'options.allowFilter', true);
        if (allowFilter) {
          hasFilters = true;
          return false;
        }
      });

      return hasFilters;
    }

    function updateFilters() {
      vm.displayFilters = [];

      if (filters) {
        vm.filtersApplied = filters.quickFilter || _.get(filters, 'mergedFilter.and', []).length > 0;

        let adhocFilters = _.get(filters, 'adhocFilter.and', []);
        let adhocDisplayFilters = getDisplayFilters(adhocFilters, false, true);
        _.forEach(adhocDisplayFilters, displayFilter => {
          displayFilter.type = ADHOC_FILTER;
          vm.displayFilters.push(displayFilter);
          vm.isAdhoc = true;
        });

        let namedFilter = _.get(filters, 'namedFilter.display');
        if (namedFilter) {
          let title = namedFilter.title;
          if (namedFilter.titleKey) {
            title = $translate.instant(namedFilter.titleKey);
          }

          let namedDisplayFilter = {
            id: filters.namedFilter.id,
            internalId: NAMED_FILTER,
            type: NAMED_FILTER,
            display: $translate.instant('uxf.uxf.FilterBuiltIn', [title]),
            tooltip: $translate.instant('uxf.uxf.FilterBuiltInTooltip', [title])
          };

          let namedFilters = _.get(filters, 'namedFilter.and');
          if (namedFilters) {
            namedDisplayFilter.displayFilters = getDisplayFilters(namedFilters);
          }

          vm.displayFilters.push(namedDisplayFilter);
        }

        let userFilters = _.get(filters, 'userFilter.and', []);
        let userDisplayFilters = getDisplayFilters(userFilters);
        _.forEach(userDisplayFilters, displayFilter => {
          vm.displayFilters.push(displayFilter);
        });
      }
      else {
        vm.filtersApplied = false;
      }
    }


    function getDisplayFilters(items, isSubFilter = false, isAdhoc = false) {
      let displayFilters = [];
      _.forEach(items, item => {
        let or = _.get(item, 'or');
        let and = _.get(item, 'and');
        if(!or && !and && isAdhoc) {
          displayFilters.push(item);
        }
        else if (or) {

          let columnId = or[0].columnId;
          let columnTitle = getColumnTitle(columnId);
          let matchAnyFilter = {
            id: columnId,
            type: MATCH_ANY,
            display: $translate.instant('uxf.uxf.FilterMatchAny', [columnTitle]),
            tooltip: $translate.instant('uxf.uxf.FilterMatchAnyTooltip', [columnTitle, or.length]),
          };

          matchAnyFilter.displayFilters = getDisplayFilters(or, true);

          displayFilters.push(matchAnyFilter);
        }
        else if (and) {
          let columnId = and[0].columnId;
          let columnTitle = getColumnTitle(columnId);
          let excludeFilter = {
            id: columnId,
            type: EXCLUDE,
            display: $translate.instant('uxf.uxf.FilterExclude', [columnTitle]),
            tooltip: $translate.instant('uxf.uxf.FilterExcludeTooltip', [columnTitle, and.length]),
          };

          excludeFilter.displayFilters = getDisplayFilters(and, true);

          displayFilters.push(excludeFilter);
        }
        else {
          item.type = USER_FILTER;
          setupDisplayStrings(item, isSubFilter);
          displayFilters.push(item);
        }
      });

      return displayFilters;
    }

    function updateQuickAccessFilters() {
      if (vm.gridOptions) {
        _.forEach(vm.gridOptions.columns, column => {
          let isQuickAccess = _.get(column, 'options.filter.isQuickAccess', false);
          if (isQuickAccess) {
            let index = _.get(column, 'options.filter.quickAccessIndex', 0);
            let filter = {
              id: column.id,
              display: $gzFields.resolveString(column.header, 'title', column.id),
              index: column.options.filter.quickAccessIndex
            };

            vm.quickAccessFilters.push(filter);
          }
        });

        vm.quickAccessFilters = _.sortBy(vm.quickAccessFilters, 'index');
      }
    }

    function getColumnTitle(columnId) {
      let column = _.find(vm.gridOptions.columns, {id: columnId});
      let title = _.get(column, 'header.title');
      if (title === undefined) {
        title = $gzFields.resolveString(column.header, 'title', column.id);
        _.set(column, 'header.title', title);
      }

      return title;
    }

    function setupDisplayStrings(filter, isSubFilter) {
      if (vm.gridOptions) {
        let column = _.find(vm.gridOptions.columns, {id: filter.columnId});
        let columnTitle = getColumnTitle(filter.columnId);
        let value = filter.value;
        let begin = filter.begin;
        let end = filter.end;
        switch (column.type) {
          case 'date':
            value = moment(value).format('LL');
            begin = moment(begin).format('LL');
            end = moment(end).format('LL');
            break;
          case 'duration':
            let myColumn = _.cloneDeep(column);
            delete myColumn.field.callbacks;
            _.set(myColumn, 'field.duration.display', 'short');
            _.set(myColumn, 'field.duration.dropZero', true);
            _.set(myColumn, 'field.duration.maxUnits', 4);
            value = $gzFields.getValue(value, null, myColumn);
            break;
          case 'mapped':
            switch (column.field.mapped.display) {
              case 'active':
                if (value) {
                  value = $translate.instant('uxf.uxf.Grid_Active');
                }
                else {
                  value = $translate.instant('uxf.uxf.Grid_Inactive');
                }
                break;
              case 'on':
                if (value) {
                  value = $translate.instant('uxf.uxf.Grid_On');
                }
                else {
                  value = $translate.instant('uxf.uxf.Grid_Off');
                }
                break;
              case 'yes':
                if (value) {
                  value = $translate.instant('uxf.uxf.Grid_Yes');
                }
                else {
                  value = $translate.instant('uxf.uxf.Grid_No');
                }
                break;
              case 'locked':
                if (value) {
                  value = $translate.instant('uxf.uxf.Grid_Locked');
                }
                else {
                  value = $translate.instant('uxf.uxf.Grid_Unlocked');
                }
                break;
              case 'status':
                switch (value.toUpperCase()) {
                  case 'OK':
                    value = $translate.instant('uxf.uxf.Grid_Ok');
                    break;
                  case 'WARN':
                    value = $translate.instant('uxf.uxf.Grid_Warn');
                    break;
                  case 'ERROR':
                    value = $translate.instant('uxf.uxf.Grid_Error');
                    break;
                  case 'INFO':
                    value = $translate.instant('uxf.uxf.Grid_Info');
                    break;
                  default:
                    value = $translate.instant('uxf.uxf.Grid_Unknown');
                    break;
                }
                break;
              case 'lookup':
                let lookup = _.get(column, 'field.mapped.lookup', {title: ''});
                value = $gzFields.resolveString(column.field.mapped.lookup, 'title', value);
                break;
              case 'values':
                let found = _.find(column.field.mapped.values, {value: value});
                if (found) {
                  value = $gzFields.resolveString(found, 'title', value);
                }
                else {
                  value = '';
                }
                break;
            }
            break;
        }

        switch (filter.op) {
          case 'equals':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterShort', [columnTitle, value]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterEquals', [columnTitle, value]));
            break;
          case 'notEqual':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterShort', [columnTitle, value]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterNotEquals', [columnTitle, value]));
            break;
          case 'isEmpty':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterEmpty', [columnTitle]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterEmpty', [columnTitle]));
            break;
          case 'isNotEmpty':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterNotEmpty', [columnTitle]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterNotEmpty', [columnTitle]));
            break;
          case 'contains':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterShort', [columnTitle, value]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterContains', [columnTitle, value]));
            break;
          case 'startsWith':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterShort', [columnTitle, value]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterStartsWith', [columnTitle, value]));
            break;
          case 'endsWith':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterShort', [columnTitle, value]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterEndsWith', [columnTitle, value]));
            break;
          case 'greaterThan':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterShort', [columnTitle, value]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterGreaterThan', [columnTitle, value]));
            break;
          case 'greaterThanOrEqual':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterShort', [columnTitle, value]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterGreaterThanOrEquals', [columnTitle, value]));
            break;
          case 'lessThan':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterShort', [columnTitle, value]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterLessThan', [columnTitle, value]));
            break;
          case 'lessThanOrEqual':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterShort', [columnTitle, value]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterLessThanOrEquals', [columnTitle, value]));
            break;
          case 'between':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterShortBetween', [columnTitle, begin, end]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterBetween', [columnTitle, begin, end]));
            break;
          case 'thisWeek':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterThisWeek', [columnTitle]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterThisWeek', [columnTitle]));
            break;
          case 'nextWeek':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterNextWeek', [columnTitle]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterNextWeek', [columnTitle]));
            break;
          case 'lastWeek':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterLastWeek', [columnTitle]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterLastWeek', [columnTitle]));
            break;
          case 'today':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterToday', [columnTitle]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterToday', [columnTitle]));
            break;
          case 'tomorrow':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterTomorrow', [columnTitle]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterTomorrow', [columnTitle]));
            break;
          case 'yesterday':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterYesterday', [columnTitle]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterYesterday', [columnTitle]));
            break;
          case 'future':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterFuture', [columnTitle]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterFuture', [columnTitle]));
            break;
          case 'past':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterPast', [columnTitle]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterPast', [columnTitle]));
            break;
          case 'lastSevenDays':
            _.set(filter, 'display', $translate.instant('uxf.uxf.FilterLastSevenDays', [columnTitle]));
            _.set(filter, 'tooltip', $translate.instant('uxf.uxf.FilterLastSevenDays', [columnTitle]));
            break;
        }
      }
    }

    function handleUserFilterChanged(event, data) {
      if (data.pairingId === vm.pairingId) {
        filters.userFilter = _.cloneDeep(data.userFilter);
        $gzFilters.mergeFilters(filters);

        if (!data.suppressFilterBarRefresh) {
          vm.selectedFilter1 = undefined;
          vm.selectedFilter2 = undefined;
        }
        updateFilters();
      }
    }

    function handleNamedFilterChanged(event, data) {
      if (data.pairingId === vm.pairingId) {
        filters.namedFilter = _.cloneDeep(data.namedFilter);
        if (data.namedFilter) {
          filters.userFilter = {and: []};
        }
        $gzFilters.mergeFilters(filters);
        if(vm.selectedFilter1 && vm.selectedFilter1.type === NAMED_FILTER) {
          vm.selectedFilter1 = undefined;
          vm.selectedFilter2 = undefined;
        }
        updateFilters();
      }
    }

    function handleAdhocFilterChanged(event, data) {
      if (data.pairingId === vm.pairingId) {
        filters.adhocFilter = _.cloneDeep(data.adhocFilter);
        $gzFilters.mergeFilters(filters);
        updateFilters();
      }
    }

    function handleQuickFilterChanged(event, data) {
      if (data.pairingId === vm.pairingId) {
        filters.quickFilter = _.cloneDeep(data.quickFilter);
        updateFilters();
      }
    }

    function handleFilterCountChanged(event, data) {
      if (data.pairingId === vm.pairingId) {
        vm.counts = _.cloneDeep(data.counts);
      }
    }

    function handleSavedFilterAdded(event, data){
      if (data.pairingId === vm.pairingId) {
        //Adding the newly added user filter to saved filter so that it should reflect in grid my filter menu
        vm.savedFilters.push(data);
        vm.savedFilters = _.sortBy(vm.savedFilters, filter => {
          return filter.namedFilter.display;
        });

        loadSavedFilters();
      }
    }

    function toggleFilter(event, filter) {
      event.stopPropagation();
      if (vm.selectedFilter1 && vm.selectedFilter1.type === filter.type) {
        vm.selectedFilter1 = undefined;
      }
      else {
        vm.selectedFilter1 = filter;
      }
      vm.selectedFilter2 = undefined;
    }

    function toggleFilter1(event, filter) {
      event.stopPropagation();
      if (vm.selectedFilter2 && vm.selectedFilter2.type === filter.type) {
        vm.selectedFilter2 = undefined;
      }
      else {
        vm.selectedFilter2 = filter;
      }
    }

    function isExpandable(filter) {
      return (filter.type === NAMED_FILTER || filter.type === MATCH_ANY || filter.type === EXCLUDE);
    }

    function isExpanded(filter) {
      return (vm.selectedFilter1 !== undefined && vm.selectedFilter1.type === filter.type);
    }

    function isExpanded1(filter) {
      return (vm.selectedFilter2 !== undefined && vm.selectedFilter2.type === filter.type);
    }

    function selectFilter(event, filter) {
      if (filter.type === ADHOC_FILTER) {
        return;
      }

      if (filter.type === USER_FILTER) {
        selectFilterById(filter.columnId);
      }
      else {
        toggleFilter(event, filter);
      }
    }

    function selectFilter1(event, filter) {
      if (filter.type === USER_FILTER) {
        selectFilterById(filter.columnId);
      }
      else {
        toggleFilter1(event, filter);
      }
    }

    function selectFilterById(id) {

      if (vm.gridOptions) {
        $gzFilters.openFilterDialog(vm.pairingId, vm.gridOptions.columns, filters, id);
      }
    }

    function blowUpNamedFilter() {
      let namedId = filters.namedFilter.id;
      filters.userFilter = filters.mergedFilter;
      delete filters.namedFilter;

      // Tell listeners that named filter was removed so they can clear state
      $rootScope.$emit('gzNamedFilterChangedEvent', {
        pairingId: vm.pairingId,
        suppressFilterBarRefresh: true
      });

      // Tell listeners that the user filters have changed (merged with what remains of named filter)
      $rootScope.$emit('gzUserFilterChangedEvent', {
        pairingId: vm.pairingId,
        userFilter: _.cloneDeep(filters.mergedFilter)
      });
    }

    function removeFilter1(displayFilter, $event) {
      $event.stopPropagation();
      if (vm.selectedFilter1.type === NAMED_FILTER) {
        let and = _.get(filters, 'mergedFilter.and', []);
        switch(displayFilter.type) {
          case USER_FILTER:
            removeFilterFromFilters(and, displayFilter.internalId);
            adjustForSingleAndOrFilters(and);
            break;
          case MATCH_ANY:
            _.remove(and, item => {
              return (item.or && item.or[0].columnId === displayFilter.id);
            });
            break;
          case EXCLUDE:
            _.remove(and, item => {
              return (item.and && item.and[0].columnId === displayFilter.id);
            });
            break;
        }

        blowUpNamedFilter();

        vm.selectedFilter1 = undefined;
        if (displayFilter === vm.selectedFilter2) {
          vm.selectedFilter2 = undefined;
        }

        $gzAnalytics.eventClick(`Grid (${vm.pairingId}) - Clear filter`, {label: `Filter: ${displayFilter.tooltip}`});
      }
      else {
        removeFilter(displayFilter);
        vm.selectedFilter1 = _.find(vm.displayFilters, {id: vm.selectedFilter1.id});
        if (vm.selectedFilter1.displayFilters.length < 2) {
          vm.selectedFilter1 = undefined;
        }
      }
      updateFilters();
    }

    function removeFilter2(displayFilter) {
      //This has to be a Multi Match Any or Multi Exclude that is being removed from a named filter
      let and = _.get(filters, 'mergedFilter.and', []);
      removeFilterFromFilters(and, displayFilter.internalId);
      adjustForSingleAndOrFilters(and);

      blowUpNamedFilter();

      vm.selectedFilter1 = undefined;
      vm.selectedFilter2 = undefined;

      updateFilters();
      $gzAnalytics.eventClick(`Grid (${vm.pairingId}) - Clear filter`, {label: `Filter: ${displayFilter.tooltip}`});
    }

    function removeFilter(displayFilter) {
      if(displayFilter.columnId){
        $gzAnalytics.eventClick(`Grid (${vm.pairingId}) - Clear user filter`, {label: `ColId: ${displayFilter.columnId}`});
      }

      switch(displayFilter.type) {
        case QUICK_FILTER:
          delete filters.quick;
          delete filters.quick;
          $rootScope.$emit('gzQuickFilterChangedEvent', {
            pairingId: vm.pairingId
          });
          $gzAnalytics.eventClick(`Grid (${vm.pairingId}) - Clear quick filter`, {label: `Filter: ${displayFilter.display}`});
          break;
        case ADHOC_FILTER:
          let and = _.get(filters, 'adhocFilter.and', []);
          removeFilterFromFilters(and, displayFilter.internalId);
          $rootScope.$emit('gzAdhocFilterChangedEvent', {
            pairingId: vm.pairingId,
            adhocFilter: _.cloneDeep(filters.adhocFilter),
            suppressFilterBarRefresh: true
          });
          $gzAnalytics.eventClick(`Grid (${vm.pairingId}) - Clear adhoc filter`, {label: `FilterId: ${displayFilter.id}, Filter: ${displayFilter.display}`});
          break;
        case USER_FILTER:
          and = _.get(filters, 'userFilter.and', []);
          removeFilterFromFilters(and, displayFilter.internalId);
          adjustForSingleAndOrFilters(and);
          $rootScope.$emit('gzUserFilterChangedEvent', {
            pairingId: vm.pairingId,
            userFilter: _.cloneDeep(filters.userFilter),
            suppressFilterBarRefresh: true
          });
          $gzAnalytics.eventClick(`Grid (${vm.pairingId}) - Clear user filter`, {label: `FilterId: ${displayFilter.id}, Filter: ${displayFilter.display}`});
          break;
        case MATCH_ANY:
          and = _.get(filters, 'userFilter.and', []);
          _.remove(and, item => {
            return (item.or && item.or[0].columnId === displayFilter.id);
          });
          $rootScope.$emit('gzUserFilterChangedEvent', {
            pairingId: vm.pairingId,
            userFilter: _.cloneDeep(filters.userFilter),
            suppressFilterBarRefresh: true
          });
          vm.selectedFilter1 = undefined;
          break;
        case EXCLUDE:
          and = _.get(filters, 'userFilter.and', []);
          _.remove(and, item => {
            return (item.and && item.and[0].columnId === displayFilter.id);
          });
          $rootScope.$emit('gzUserFilterChangedEvent', {
            pairingId: vm.pairingId,
            userFilter: _.cloneDeep(filters.userFilter),
            suppressFilterBarRefresh: true
          });
          break;
        case NAMED_FILTER:
          delete filters.namedFilter;
          $rootScope.$emit('gzNamedFilterChangedEvent', {
            pairingId: vm.pairingId,
            suppressFilterBarRefresh: true
          });
          $gzAnalytics.eventClick(`Grid (${vm.pairingId}) - Clear named filter`, {label: `Filter: ${displayFilter.tooltip}`});
          break;
      }

      if (displayFilter === vm.selectedFilter1) {
        vm.selectedFilter1 = undefined;
      }

      $rootScope.$emit('gzFilterRemovedEvent', {
        pairingId: vm.pairingId,
        filterId: displayFilter.id
      });

      updateFilters();
    }

    function removeFilterFromFilters(filters, id) {
      _.remove(filters, {internalId: id});
      _.forEach(filters, item => {
        if (item.or) {
          removeFilterFromFilters(item.or, id);
        }
        else if (item.and) {
          removeFilterFromFilters(item.and, id);
        }
      });
    }

    function adjustForSingleAndOrFilters(filters) {
      _.forEach(filters, item => {
        if (item.or && item.or.length === 1) {
          filters.push(item.or[0]);
        }
        if (item.and && item.and.length === 1) {
          filters.push(item.and[0]);
        }
      });

      _.remove(filters, item => {
        return (item.or && item.or.length < 2) || (item.and && item.and.length < 2);
      });
    }

    function removeAllFilters() {
      $gzAnalytics.eventClick(`Grid (${vm.pairingId}) - Clear all filters`);
      let test = _.get(filters, 'userFilter.and', []);
      if (test.length > 0) {
        $rootScope.$emit('gzUserFilterChangedEvent', {
          pairingId: vm.pairingId
        });
      }

      if (filters.quickFilter) {
        $rootScope.$emit('gzQuickFilterChangedEvent', {
          pairingId: vm.pairingId
        });
      }

      if (filters.namedFilter) {
        $rootScope.$emit('gzNamedFilterChangedEvent', {
          pairingId: vm.pairingId
        });
      }

      $rootScope.$emit('gzFilterRemoveAllEvent', {
        pairingId: vm.pairingId
      });

      filters = {};
      updateFilters();
    }

    function canSave() {
      return _.get(filters, 'mergedFilter.and', []).length > 0;
    }

    function saveFilter(){
      $gzAnalytics.eventOpen(`Grid (${vm.pairingId}) - Save filter Dialog`);
      $gzFilters.openSaveFilterDialog(vm.pairingId, filters)
        .then(function(savedFilter) {
          let data = {
            pairingId: vm.pairingId,
          };

          if (savedFilter.namedFilter) {
            data.namedFilter = savedFilter.namedFilter;
          }

          //Preventing quick filter save for filterMode=true view
          if(!vm.filterMode){
            if (savedFilter.quickFilter) {
              data.quickFilter = savedFilter.quickFilter;
            }
          }

          $rootScope.$emit('gzSavedFilterAddedEvent', data);

          if(filters.namedFilter){
            $rootScope.$emit('gzFilterRemovedEvent', {
              pairingId: vm.pairingId
            });
          }

          $rootScope.$emit('gzNamedFilterChangedEvent', {
            pairingId: vm.pairingId,
            namedFilter: savedFilter.namedFilter
          });

          loadSavedFilters();
        });
    }

    //Grid's server Filters
    function loadSavedFilters(){
      return rnHttp.get(PORTAL_SERVICE_NAME, FILTERS_PATH)
        .then((response) => {
          if(response){
            let myFilters = _.filter(response.data.filters, {pairingId: vm.pairingId});
            //Fix up display
            _.forEach(myFilters, filter => {
              let display = {
                title: filter.name
              };
              _.set(filter, 'namedFilter.display', display);
            });
            vm.savedFilters = _.sortBy(myFilters, filter => {
              return filter.namedFilter.display;
            });
          }
        })
        .catch(error => $log.error('Unable to retrieve saved filter', error));
    }

    function gzSavedFilterClicked($event, filter){
      $gzAnalytics.eventClick(`Grid (${vm.pairingId}) - Saved Filter Clicked`, {label: `Filter: ${filter.id}` });
      $rootScope.$emit('gzNamedFilterChangedEvent', {
        pairingId: vm.pairingId,
        namedFilter: filter.namedFilter
      });

      if(filter.quickFilter){
        $rootScope.$emit('gzQuickFilterChangedEvent', {
          pairingId: vm.pairingId,
          quickFilter: filter.quickFilter,
          force: true
        });
      }
    }

    function gzNamedFilterClicked($event, filter){
      $gzAnalytics.eventClick(`Grid (${vm.pairingId}) - Named Filter Clicked`, {label: `Filter: ${filter.id}` });
      $rootScope.$emit('gzNamedFilterChangedEvent', {
        pairingId: vm.pairingId,
        namedFilter: filter
      });
    }

    function gzRemoveFilterClicked($event, filter){
      $event.stopPropagation();
      $event.preventDefault();
      if(filter && filter.id){
        rnHttp.delete(PORTAL_SERVICE_NAME, `${FILTERS_PATH}/${filter.id}`).then(() => {
          _.remove(vm.savedFilters, {id: filter.id});

          let message = filter.name + ' '+ $filter('translate')('uxf.uxf.Filter_Removed');
          $mdToast.showSimple(message);
          $gzAnalytics.eventDelete(`Grid (${vm.pairingId}) - Filter ${filter.name}`) ;
        });
      }
    }

    function gzRemoveBuiltInFilterClicked($event, filter){
      if(filter && filter.id){
        $rootScope.$emit('gzFilterRemovedEvent', {
          pairingId: vm.pairingId,
          filterId: filter.id
        });

        $rootScope.$emit('gzNamedFilterChangedEvent', {
          pairingId: vm.pairingId,
          userFilter: filters.namedFilter
        });
      }
    }

    function gzFilterClicked($event, colId = undefined) {
      $gzFilters.openFilterDialog(vm.pairingId, vm.gridOptions.columns, filters, colId);
    }
  }
}



// WEBPACK FOOTER //
// ../~/@printos/gazelle/src/components/filter-bar/filter-bar.directive.js